<template>
  <MDBCard v-if="context.canSeeKickbackFeeBox" class="score-container mt-3 w-100" :class="[
    {
      intro__step: user?.intro == 3,
    },
  ]">
    <MDBCardBody>
      <MDBCardTitle class="title title-slash text-uppercase font-12 font-md-15 font-xxl-25">
        {{ $t("dashboard.bankstatement.yourKickback") }}
      </MDBCardTitle>
      <MDBCardText>
        <div class="fw-bold" v-if="payoutItems.length">
          <div class="font-12 text-dark-gray" v-for="(payout, index) in payoutItems" :key="index">
            {{ getPayoutYear(payout) }}: &euro; {{ getPayoutAmount(payout) }} {{ $t('global.paidOut') }}
          </div>
        </div>
        <div class="fw-bold" v-if="kickbackItems.length">
          <div v-for="(data, index) in kickbackItems" :key="index" :class="[
            { 'font-18 font-md-26 success-green': currentYear == getKickbackYear(data) }
          ]">
            {{ getKickbackYear(data) }}: &euro; {{
              formatFee(user.display_kickbacks ? getKickbackAmount(data) : 0) }}
          </div>
        </div>
        <p class="font-12">
          {{ $t("dashboard.bankstatement.lastUpdate") }}
          {{
            lastUpdate?.date
              ? dates.parseDate(lastUpdate?.date)
              : dates.parseDate(new Date())
          }}
        </p>
      </MDBCardText>
    </MDBCardBody>
  </MDBCard>
</template>

<script setup>
import {
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBCardText,
} from "mdb-vue-ui-kit";
import { useStore } from "vuex";
import { ref, watch, computed } from "vue";
import dates from "@/plugins/dates";
import { useItemsConditions } from '@/composables/useItemsConditions'

const store = useStore();
const { getContext } = useItemsConditions()
const context = computed(() => getContext())

const user = computed(() => store.getters.getMe);
const isStaff = ref(false);
const currentYear = new Date().getFullYear()
const previousYear = currentYear - 1;

const payoutYearSet = computed(() => {
  const payouts = user.value?.payouts;
  if (!Array.isArray(payouts)) {
    return new Set();
  }

  return payouts.reduce((set, payout) => {
    if (payout && typeof payout === "object") {
      const key = Object.keys(payout)[0];
      if (key) {
        set.add(String(key).replace(":", ""));
      }
    }
    return set;
  }, new Set());
});
const payoutItems = computed(() => {
  const payouts = user.value?.payouts;
  if (!Array.isArray(payouts)) {
    return [];
  }

  return payouts.filter((payout) => getPayoutYear(payout) === previousYear);
});
const kickbackItems = computed(() => {
  const kickbacks = user.value?.kick_back_by_year;
  if (!kickbacks) {
    return [];
  }

  const items = Array.isArray(kickbacks) ? kickbacks : Object.values(kickbacks);
  const filteredItems = payoutYearSet.value.size
    ? items.filter((item) => {
      const year = getKickbackYear(item);
      if (year === null || year === undefined) {
        return true;
      }
      return !payoutYearSet.value.has(String(year));
    })
    : items;
  const hasCurrentYear = items.some((item) => getKickbackYear(item) === currentYear);
  if (!hasCurrentYear && !payoutYearSet.value.has(String(currentYear))) {
    filteredItems.push({ year: currentYear, kick_back_fee_sum: 0 });
  }

  return filteredItems.filter((item) => {
    const year = getKickbackYear(item);
    return year === currentYear || year === previousYear;
  });
});

watch(user, async (newVal) => {
  if (newVal && newVal.roles.find((item) => item.name == "staff")) {
    isStaff.value = true;
  }
});
const formatFee = (value) => (value ?? 0).toFixed(2).replace('.', ',');
const lastUpdate = computed(() => store.getters.getLastBankStatement);
const getKickbackYear = (item) => {
  if (!item || typeof item !== "object") {
    return null;
  }
  if (item.year !== undefined && item.year !== null) {
    return item.year;
  }
  const key = Object.keys(item)[0];
  return key ? Number(String(key).replace(":", "")) : null;
};
const getPayoutYear = (item) => {
  if (!item || typeof item !== "object") {
    return null;
  }
  const key = Object.keys(item)[0];
  return key ? Number(String(key).replace(":", "")) : null;
};
const getPayoutAmount = (item) => {
  if (!item || typeof item !== "object") {
    return 0;
  }
  const value = Object.values(item)[0];
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? 0 : numericValue;
};
const getKickbackAmount = (item) => {
  if (!item || typeof item !== "object") {
    return 0;
  }
  if (item.kick_back_fee !== undefined && item.kick_back_fee !== null) {
    return item.kick_back_fee;
  }
  if (item.kick_back_fee_sum !== undefined && item.kick_back_fee_sum !== null) {
    return item.kick_back_fee_sum;
  }
  const value = Object.values(item)[0];
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? 0 : numericValue;
};

</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.score-container {
  width: 100%;
  box-sizing: border-box;
  flex: 1 1 auto;
  align-self: stretch;
  height: auto !important;
  min-height: $nlBannerHeight;
  max-height: $nlBannerHeight;
}
</style>
